import { escapeAttr } from "./domUtils";
import { isProviderApiError, ModelInfo } from "../../core/adapters";
import { getStrings } from "../../core/i18n";

export interface ModelComboboxOptions {
  initialValue: string;
  placeholder: string;
  fetchModels: () => Promise<ModelInfo[]>;
  onError: (message: string) => void;
  onErrorClear: () => void;
  // If provided and returns a non-null string, clicking the dropdown arrow before any models
  // have been fetched shows that message instead of fetching (e.g. "enter an API key first").
  getFetchBlockedReason?: () => string | null;
  // Fired whenever the field's value changes, whether typed or picked from the suggestion
  // list — lets the parent form (e.g. the Max context placeholder) react live to the current
  // model rather than only to provider/endpoint changes.
  onValueChange?: (value: string) => void;
}

export interface ModelComboboxHandle {
  getValue(): string;
  hasFetchedModels(): boolean;
  isKnownModel(value: string): boolean;
  // The live context-size hint for a fetched model, if the provider's API returned one (see
  // core/adapters/types.ts's ModelInfo) — undefined if the model hasn't been fetched or the
  // provider doesn't expose this.
  getContextTokensFor(value: string): number | undefined;
  // Same idea, for the reply-length cap (ModelInfo.maxOutputTokens) — currently only ever
  // populated for Anthropic models.
  getMaxOutputTokensFor(value: string): number | undefined;
  refetch(): Promise<ModelInfo[] | null>;
}

// Shared "editable combobox" model picker: a text field you can always type in, plus a
// dropdown arrow that fetches/shows the provider's live model list. Used by both the provider
// form and the new-session picker so they share one implementation of this fairly involved
// widget (fetch state, suggestion filtering, the blur/mousedown race fix, etc.) rather than two.
export function createModelCombobox(
  container: HTMLElement,
  options: ModelComboboxOptions
): ModelComboboxHandle {
  const s = getStrings();
  container.innerHTML = `
    <div class="model-combobox">
      <input
        type="text"
        class="model-combobox-input"
        required
        autocomplete="off"
        value="${escapeAttr(options.initialValue)}"
        placeholder="${escapeAttr(options.placeholder)}"
      />
      <button type="button" class="combobox-toggle" aria-label="${s.modelCombobox.showModelListAria}">&#9662;</button>
      <ul class="model-suggestions" role="listbox" style="display:none;"></ul>
    </div>
  `;

  const modelInput = container.querySelector(".model-combobox-input") as HTMLInputElement;
  const dropdownToggleBtn = container.querySelector(".combobox-toggle") as HTMLButtonElement;
  const suggestionsList = container.querySelector(".model-suggestions") as HTMLUListElement;

  let fetchedModels: ModelInfo[] = [];
  let hideSuggestionsTimeoutId: number | undefined;

  function renderSuggestions(filterText: string): void {
    const query = filterText.trim().toLowerCase();
    const filtered = query
      ? fetchedModels.filter((m) => m.id.toLowerCase().includes(query))
      : fetchedModels;
    if (filtered.length === 0) {
      suggestionsList.style.display = "none";
      suggestionsList.innerHTML = "";
      return;
    }
    suggestionsList.innerHTML = filtered
      .map(
        (m) =>
          `<li class="model-suggestion" data-value="${escapeAttr(m.id)}">${escapeAttr(m.id)}</li>`
      )
      .join("");
    suggestionsList.style.display = "";
  }

  function hideSuggestions(): void {
    suggestionsList.style.display = "none";
  }

  function scheduleHideSuggestions(): void {
    hideSuggestionsTimeoutId = window.setTimeout(() => {
      hideSuggestions();
      hideSuggestionsTimeoutId = undefined;
    }, 150);
  }

  function cancelScheduledHide(): void {
    if (hideSuggestionsTimeoutId !== undefined) {
      window.clearTimeout(hideSuggestionsTimeoutId);
      hideSuggestionsTimeoutId = undefined;
    }
  }

  async function fetchModelList(): Promise<ModelInfo[] | null> {
    options.onErrorClear();
    dropdownToggleBtn.disabled = true;
    const originalGlyph = dropdownToggleBtn.innerHTML;
    dropdownToggleBtn.innerHTML = "&hellip;";
    try {
      const models = await options.fetchModels();
      if (models.length === 0) {
        options.onError(s.modelCombobox.noModelsReturned);
        return null;
      }
      fetchedModels = models;
      return models;
    } catch (err) {
      options.onError(isProviderApiError(err) ? err.message : s.modelCombobox.fetchFailed);
      return null;
    } finally {
      dropdownToggleBtn.innerHTML = originalGlyph;
      dropdownToggleBtn.disabled = false;
    }
  }

  modelInput.addEventListener("input", () => {
    if (fetchedModels.length > 0) renderSuggestions(modelInput.value);
    options.onValueChange?.(modelInput.value.trim());
  });

  modelInput.addEventListener("focus", () => {
    cancelScheduledHide();
    if (fetchedModels.length > 0) renderSuggestions(modelInput.value);
  });

  // A short delay (instead of hiding immediately) lets the suggestion list's own
  // mousedown handler below run first and fill the field before we hide it.
  modelInput.addEventListener("blur", () => {
    scheduleHideSuggestions();
  });

  // Without this, clicking the toggle blurs the model input first (since focus is about to
  // shift to this button), which schedules the hide-on-blur timer above — that timer then
  // fires shortly after the click handler below reopens the list, closing it again.
  dropdownToggleBtn.addEventListener("mousedown", (e) => {
    e.preventDefault();
  });

  dropdownToggleBtn.addEventListener("click", async () => {
    cancelScheduledHide();
    if (suggestionsList.style.display !== "none") {
      hideSuggestions();
      return;
    }
    if (fetchedModels.length === 0) {
      const blockedReason = options.getFetchBlockedReason?.();
      if (blockedReason) {
        options.onError(blockedReason);
        return;
      }
      const models = await fetchModelList();
      if (!models) return;
    }
    renderSuggestions(modelInput.value);
    modelInput.focus();
  });

  // mousedown (not click) fires before the input's blur, so the value swap below
  // happens before blur's hide-timeout would otherwise race it.
  suggestionsList.addEventListener("mousedown", (e) => {
    const target = e.target as HTMLElement;
    if (!target.classList.contains("model-suggestion")) return;
    e.preventDefault();
    const value = target.getAttribute("data-value") || "";
    modelInput.value = value;
    hideSuggestions();
    options.onValueChange?.(value);
  });

  return {
    getValue: () => modelInput.value.trim(),
    hasFetchedModels: () => fetchedModels.length > 0,
    isKnownModel: (value: string) => fetchedModels.some((m) => m.id === value),
    getContextTokensFor: (value: string) =>
      fetchedModels.find((m) => m.id === value)?.contextTokens,
    getMaxOutputTokensFor: (value: string) =>
      fetchedModels.find((m) => m.id === value)?.maxOutputTokens,
    refetch: () => fetchModelList(),
  };
}
